import { useState } from 'react';

// json import removed - return plain objects in React Router v7
import type { LoaderFunctionArgs, MetaFunction } from 'react-router';
import { Link } from 'react-router';
import { motion } from 'framer-motion';
import { ArrowLeft, Check, Clock, Share2 } from 'lucide-react';
import { toast } from 'sonner';

import Header from '~/components/Header';
import { createLogger } from '~/utils/logger.server';

export const handle = {
  title: 'How to Speak Confidently in Meetings (Even When Put on the Spot) | Pavone Academy',
  description: 'Learn how to speak confidently in meetings with practical tips for sounding professional at work. Improve communication in daily meetings and handle being put on the spot.',
};

const logger = createLogger('AcademyMeetingsArticle');

export async function loader(args: LoaderFunctionArgs) {
  await logger.info('Academy meetings confidence article loaded');
  return Response.json({});
}

export const meta: MetaFunction = () => {
  return [
    { title: 'Speak Confidently in Meetings - Pavone Academy' },
    { 
      name: 'description', 
      content: 'Master how to speak confidently in meetings, even when put on the spot. Practical tips to sound professional and improve communication at work.' 
    },
    { name: 'keywords', content: 'how to speak confidently in meetings, improve communication in meetings, how to sound professional at work, daily meeting confidence tips, AI communication coach' },
    { property: 'og:title', content: 'How to Speak Confidently in Meetings (Even When Put on the Spot)' },
    { property: 'og:description', content: 'Practical strategies to speak with confidence in daily meetings and handle unexpected questions like a pro.' },
    { property: 'og:type', content: 'article' },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: 'Speak Confidently in Meetings' },
    { name: 'twitter:description', content: 'Learn how to sound professional in meetings, even when put on the spot.' },
  ];
};

export default function MeetingsConfidenceArticle() {
  const [isShared, setIsShared] = useState(false);

  const handleShare = async () => {
    const url = window.location.href;
    const title = 'How to Speak Confidently in Meetings (Even When Put on the Spot)';
    const text = 'Check out these meeting confidence tips:';

    try {
      if (navigator.share) {
        await navigator.share({
          title,
          text,
          url,
        });
        setIsShared(true);
        toast.success('Article shared!');
        setTimeout(() => setIsShared(false), 2000);
      } else {
        await navigator.clipboard.writeText(url);
        setIsShared(true);
        toast.success('Link copied to clipboard!');
        setTimeout(() => setIsShared(false), 2000);
      }
    } catch (error) {
      if (error instanceof Error && error.name !== 'AbortError') {
        try {
          await navigator.clipboard.writeText(url);
          setIsShared(true);
          toast.success('Link copied to clipboard!');
          setTimeout(() => setIsShared(false), 2000);
        } catch (clipboardError) {
          toast.error('Failed to share. Please copy the link manually.');
        }
      }
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />
      
      <main className="flex-1 pt-24 pb-16">
        <article className="max-w-[800px] mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back Button */}
          <Link
            to="/academy"
            className="inline-flex items-center gap-2 text-brand-dark-blue hover:text-brand-gold transition-colors mb-8"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="font-medium">Back to Academy</span>
          </Link>

          {/* Hero Image */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-12 rounded-2xl overflow-hidden"
          >
            <img
              src="/images/academy/zoom.jpg"
              alt="Professional speaking confidently in a meeting"
              className="w-full h-[300px] sm:h-[400px] lg:h-[500px] object-cover"
            />
          </motion.div>

          {/* Article Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center gap-3 mb-4">
              <span className="text-xs font-semibold text-brand-gold bg-brand-gold/10 px-3 py-1 rounded-full">
                Professional Skills
              </span>
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Clock className="h-4 w-4" />
                <span>7 min read</span>
              </div>
            </div>

            <h1 className="text-4xl sm:text-5xl font-bold text-brand-dark-blue mb-6 leading-tight">
              How to Speak Confidently in Meetings (Even When You're Put on the Spot)
            </h1>

            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              Master daily meeting confidence with practical strategies to sound professional, articulate, and 
              composed — even when your manager unexpectedly asks for your opinion.
            </p>

            <div className="flex items-center justify-between py-4 border-y border-gray-200 mb-12">
              <div className="text-sm text-gray-600">
                <span className="font-medium">Published:</span> November 8, 2024
              </div>
              <button
                onClick={handleShare}
                className={`flex items-center gap-2 text-sm transition-colors ${
                  isShared
                    ? 'text-brand-green'
                    : 'text-gray-600 hover:text-brand-dark-blue'
                }`}
              >
                {isShared ? (
                  <>
                    <Check className="h-4 w-4" />
                    <span>Copied!</span>
                  </>
                ) : (
                  <>
                    <Share2 className="h-4 w-4" />
                    <span>Share</span>
                  </>
                )}
              </button>
            </div>
          </motion.div>

          {/* Article Content */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="prose prose-lg max-w-none"
          >
            {/* Hook - Relatable Scenario */}
            <p className="text-lg leading-relaxed text-gray-700 mb-6">
              That moment when your manager says, <em>"What do you think about this?"</em> and suddenly everyone's 
              looking at you. Your mind goes blank. Your heart starts racing. You open your mouth and out comes: 
              <em>"Um... yeah... I think... well..."</em>
            </p>

            <p className="text-lg leading-relaxed text-gray-700 mb-6">
              You had thoughts two seconds ago. Good ones. But now? Nothing. And the longer the silence stretches, 
              the worse it gets.
            </p>

            <p className="text-lg leading-relaxed text-gray-700 mb-8">
              If this sounds familiar, you're not alone. <strong>The fear of speaking up in meetings is one of the 
              most common workplace anxieties.</strong> And here's what makes it harder: unlike presentations where 
              you can prepare, meetings catch you off guard. Someone asks a direct question. The conversation shifts 
              unexpectedly. You're suddenly on the spot with no script.
            </p>

            <p className="text-lg leading-relaxed text-gray-700 mb-8">
              But here's the good news: <strong>learning how to speak confidently in meetings is a skill you can 
              train.</strong> Not by becoming a different person. Not by faking confidence. By using specific 
              techniques that make you sound clear, composed, and professional — even when your brain is scrambling.
            </p>

            {/* Strategy 1 */}
            <h2 className="text-3xl font-bold text-brand-dark-blue mt-12 mb-6">
              Strategy #1: Keep Mental Bullet Points (Not Full Scripts)
            </h2>

            <p className="text-lg leading-relaxed text-gray-700 mb-6">
              The biggest mistake people make when trying to improve communication in meetings? They try to script 
              everything perfectly in their head. Full sentences. Polished phrases. The "right" way to say it.
            </p>

            <p className="text-lg leading-relaxed text-gray-700 mb-6">
              Then someone asks a question, and the script falls apart. You panic because you're trying to remember 
              exact wording instead of just sharing your actual thoughts.
            </p>

            <h3 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">
              Use the 3-Point Rule Instead
            </h3>

            <p className="text-lg leading-relaxed text-gray-700 mb-6">
              Before any meeting, jot down 3 bullet points about each topic. Not sentences. Just key words or phrases.
            </p>

            <div className="bg-brand-dark-blue/5 p-6 rounded-lg mb-8">
              <h4 className="font-bold text-gray-900 mb-4">Example: Project Status Update</h4>
              <div className="space-y-3 text-lg text-gray-700">
                <div className="flex gap-3">
                  <span className="text-brand-dark-blue font-bold">•</span>
                  <span><strong>Progress:</strong> 70% complete, on track</span>
                </div>
                <div className="flex gap-3">
                  <span className="text-brand-dark-blue font-bold">•</span>
                  <span><strong>Blocker:</strong> Waiting on legal review</span>
                </div>
                <div className="flex gap-3">
                  <span className="text-brand-dark-blue font-bold">•</span>
                  <span><strong>Next step:</strong> Launch prep next week</span>
                </div>
              </div>
            </div>

            <p className="text-lg leading-relaxed text-gray-700 mb-6">
              When someone asks, "How's the project going?" you're not fishing for perfect words. You glance at 
              your bullet points and talk naturally: <em>"We're at 70% completion, right on schedule. The only 
              thing holding us up is the legal review, but once that's done, we'll move into launch prep next week."</em>
            </p>

            <p className="text-lg leading-relaxed text-gray-700 mb-8">
              <strong>Bullet points give you structure without rigidity.</strong> You sound prepared and confident 
              because you know your key points — but you're not tied to scripted language that crumbles under pressure.
            </p>

            {/* Strategy 2 */}
            <h2 className="text-3xl font-bold text-brand-dark-blue mt-12 mb-6">
              Strategy #2: Buy Yourself 3 Seconds With Smart Openers
            </h2>

            <p className="text-lg leading-relaxed text-gray-700 mb-6">
              You know that panic when someone asks you a question and your brain needs a second to catch up? 
              Most people fill that silence with <em>"Uhhhhh..."</em> or nervous laughter. That makes you sound 
              uncertain before you've even started.
            </p>

            <p className="text-lg leading-relaxed text-gray-700 mb-6">
              <strong>Confident speakers use "bridge phrases"</strong> — neutral openers that buy you 2-3 seconds 
              to organize your thoughts while <em>sounding</em> composed.
            </p>

            <h3 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">
              Bridge Phrases That Work
            </h3>

            <div className="space-y-4 mb-8">
              <div className="border-l-4 border-brand-gold pl-6 py-3 bg-brand-gold/5">
                <p className="text-lg text-gray-700 mb-1"><strong>"That's a great question..."</strong></p>
                <p className="text-sm text-gray-600">Gives you 2 seconds while showing you're engaged.</p>
              </div>

              <div className="border-l-4 border-brand-gold pl-6 py-3 bg-brand-gold/5">
                <p className="text-lg text-gray-700 mb-1"><strong>"Let me think about that for a second..."</strong></p>
                <p className="text-sm text-gray-600">Shows you're being thoughtful, not stalling.</p>
              </div>

              <div className="border-l-4 border-brand-gold pl-6 py-3 bg-brand-gold/5">
                <p className="text-lg text-gray-700 mb-1"><strong>"From my perspective..."</strong></p>
                <p className="text-sm text-gray-600">Frames your answer as your viewpoint (less pressure to be "right").</p>
              </div>

              <div className="border-l-4 border-brand-gold pl-6 py-3 bg-brand-gold/5">
                <p className="text-lg text-gray-700 mb-1"><strong>"Based on what we've seen so far..."</strong></p>
                <p className="text-sm text-gray-600">Grounds your answer in data/experience.</p>
              </div>

              <div className="border-l-4 border-brand-gold pl-6 py-3 bg-brand-gold/5">
                <p className="text-lg text-gray-700 mb-1"><strong>"I'd say the main thing is..."</strong></p>
                <p className="text-sm text-gray-600">Sets up a clear, focused answer.</p>
              </div>
            </div>

            <p className="text-lg leading-relaxed text-gray-700 mb-8">
              Notice how these phrases sound professional and intentional? They're not filler. They signal that 
              you're taking the question seriously. And they give your brain just enough time to pull your thoughts 
              together. <strong>That 3-second pause is the difference between sounding flustered and sounding 
              composed.</strong>
            </p>

            {/* Strategy 3 */}
            <h2 className="text-3xl font-bold text-brand-dark-blue mt-12 mb-6">
              Strategy #3: Slow Down (Especially When You're Nervous)
            </h2>

            <p className="text-lg leading-relaxed text-gray-700 mb-6">
              Here's what happens when you're put on the spot: your nervous system kicks in. Your heart rate goes up. 
              And without realizing it, you start talking faster. Words tumble out. You rush through sentences. 
              And ironically, <strong>the faster you talk, the less confident you sound.</strong>
            </p>

            <p className="text-lg leading-relaxed text-gray-700 mb-6">
              I coached a director, Rachel, who had this exact problem. Smart. Experienced. But in executive meetings, 
              she'd get a question and her answer would come out like rapid-fire bullets. She thought speed made her 
              sound sharp. Instead, it made her sound anxious.
            </p>

            <p className="text-lg leading-relaxed text-gray-700 mb-6">
              We worked on one thing: <strong>deliberate slowing.</strong> Not dragging out words. Just taking pauses 
              between thoughts. Speaking at the pace of someone who's confident they have the room's attention.
            </p>

            <h3 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">
              The Pause Power Technique
            </h3>

            <div className="bg-gradient-to-r from-brand-dark-blue/10 to-transparent p-6 rounded-lg mb-8">
              <p className="text-lg text-gray-700 mb-4">
                <strong>Before you speak:</strong> Take one full breath. Don't rush to fill silence.
              </p>
              <p className="text-lg text-gray-700 mb-4">
                <strong>Between points:</strong> Pause for 1-2 seconds. Let each idea land before moving to the next.
              </p>
              <p className="text-lg text-gray-700 mb-4">
                <strong>When asked a tough question:</strong> Pause for 2-3 seconds before answering. This shows 
                you're thinking, not scrambling.
              </p>
              <p className="text-lg text-gray-700">
                <strong>After making a key point:</strong> Stop. Resist the urge to keep explaining. Let your 
                point stand.
              </p>
            </div>

            <p className="text-lg leading-relaxed text-gray-700 mb-8">
              Rachel started using these pauses. Her next executive meeting? Her boss pulled her aside after and 
              said, <em>"You sounded really confident in there."</em> Same content. Same knowledge. Different pacing. 
              <strong>Silence isn't weakness. It's control.</strong>
            </p>

            {/* Strategy 4 */}
            <h2 className="text-3xl font-bold text-brand-dark-blue mt-12 mb-6">
              Strategy #4: How to Sound Professional at Work — Tone Matters More Than Words
            </h2>

            <p className="text-lg leading-relaxed text-gray-700 mb-6">
              Two people can say the exact same thing in a meeting. One sounds confident and decisive. The other 
              sounds unsure and hesitant. The difference? <strong>Tone.</strong>
            </p>

            <p className="text-lg leading-relaxed text-gray-700 mb-6">
              Most people don't realize how they actually sound in meetings. You think you're speaking clearly, 
              but your voice is quiet. You think you sound confident, but you're using "upspeak" (ending sentences 
              like questions). You don't notice these patterns because you're focused on <em>what</em> you're saying, 
              not <em>how</em>.
            </p>

            <h3 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">
              3 Tone Shifts That Make You Sound More Professional
            </h3>

            <div className="space-y-6 mb-8">
              <div className="bg-gray-50 p-6 rounded-lg">
                <h4 className="text-xl font-bold text-gray-900 mb-3">1. End Statements With Downward Inflection</h4>
                <p className="text-lg text-gray-700 mb-3">
                  ❌ <em>"I think we should launch next week?"</em> (upspeak = sounds like you're asking permission)
                </p>
                <p className="text-lg text-gray-700">
                  ✅ <em>"I think we should launch next week."</em> (downward tone = sounds decisive)
                </p>
              </div>

              <div className="bg-gray-50 p-6 rounded-lg">
                <h4 className="text-xl font-bold text-gray-900 mb-3">2. Speak Slightly Louder Than Feels Natural</h4>
                <p className="text-lg text-gray-700">
                  If you're nervous, you unconsciously lower your volume. Confident speakers project. Aim for 20% 
                  louder than your comfortable volume. It'll feel like you're being too loud — but to others, 
                  it sounds assertive.
                </p>
              </div>

              <div className="bg-gray-50 p-6 rounded-lg">
                <h4 className="text-xl font-bold text-gray-900 mb-3">3. Lower Your Pitch on Key Points</h4>
                <p className="text-lg text-gray-700">
                  High-pitched voice = nervousness. Slightly lower pitch = authority. When making your main point, 
                  drop your pitch by 10-15%. Example: <em>"The deadline is Friday"</em> (emphasize "Friday" with 
                  lower pitch).
                </p>
              </div>
            </div>

            <p className="text-lg leading-relaxed text-gray-700 mb-8">
              If you want to hear how your tone actually sounds before your next meeting, practice with tools like 
              Pavone. Record yourself answering common meeting questions and get AI feedback on whether you sound 
              confident or uncertain. <strong>You can't fix what you can't hear.</strong>
            </p>

            {/* Strategy 5 */}
            <h2 className="text-3xl font-bold text-brand-dark-blue mt-12 mb-6">
              Strategy #5: Eliminate Filler Words (Or At Least Cut Them in Half)
            </h2>

            <p className="text-lg leading-relaxed text-gray-700 mb-6">
              <em>"Um, so like, I think, you know, we should probably..."</em>
            </p>

            <p className="text-lg leading-relaxed text-gray-700 mb-6">
              Every filler word chips away at your credibility. <strong>Filler words make you sound unsure</strong> — 
              even when you're saying something smart. And most people have no idea how many they use.
            </p>

            <p className="text-lg leading-relaxed text-gray-700 mb-6">
              I worked with a product manager, David, who was brilliant. But in meetings, he'd say "um" and "like" 
              constantly. His ideas got overlooked because his delivery undermined his authority. We recorded one 
              of his updates: <strong>23 filler words in 90 seconds.</strong> He was shocked.
            </p>

            <h3 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">
              How to Cut Filler Words Without Sounding Robotic
            </h3>

            <div className="bg-brand-gold/5 p-6 rounded-lg mb-8 border-l-4 border-brand-gold">
              <ul className="space-y-3 text-lg text-gray-700">
                <li className="flex gap-3">
                  <span className="text-brand-gold font-bold">1.</span>
                  <span><strong>Replace filler with silence.</strong> When you feel "um" coming, just pause. 
                  Silence is better.</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-brand-gold font-bold">2.</span>
                  <span><strong>Slow down.</strong> Filler words happen when you're rushing. Give yourself time 
                  to think.</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-brand-gold font-bold">3.</span>
                  <span><strong>Record your next meeting (with permission).</strong> Listen back. Count your 
                  filler words. Awareness is step one.</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-brand-gold font-bold">4.</span>
                  <span><strong>Practice out loud before meetings.</strong> Run through your key points. Notice 
                  when you use fillers and practice pausing instead.</span>
                </li>
              </ul>
            </div>

            <p className="text-lg leading-relaxed text-gray-700 mb-8">
              David started recording himself before big meetings. After two weeks of deliberate practice, he cut 
              his filler words from 23 to 6 per 90 seconds. His manager noticed: <em>"You sound way more 
              confident lately."</em> Same person. Same ideas. Cleaner delivery.
            </p>

            {/* Strategy 6 */}
            <h2 className="text-3xl font-bold text-brand-dark-blue mt-12 mb-6">
              Strategy #6: Prepare Your "Go-To" Phrases for Common Situations
            </h2>

            <p className="text-lg leading-relaxed text-gray-700 mb-6">
              Certain situations come up in meetings again and again. Someone disagrees with you. You don't know 
              the answer to a question. You need to push back on an idea. Instead of improvising every time, 
              <strong>prepare your go-to responses.</strong>
            </p>

            <h3 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">
              Ready-Made Phrases for Tricky Situations
            </h3>

            <div className="space-y-6 mb-8">
              <div className="border-l-4 border-brand-dark-blue pl-6 py-2">
                <h4 className="font-bold text-gray-900 mb-2">When you don't know the answer:</h4>
                <p className="text-lg text-gray-700">
                  <em>"I don't have that data in front of me right now, but I'll follow up with you by end of day."</em>
                </p>
                <p className="text-sm text-gray-600 mt-2">
                  Honest + shows accountability. Way better than guessing.
                </p>
              </div>

              <div className="border-l-4 border-brand-dark-blue pl-6 py-2">
                <h4 className="font-bold text-gray-900 mb-2">When you disagree (but need to be diplomatic):</h4>
                <p className="text-lg text-gray-700">
                  <em>"I see where you're coming from. One thing I'm thinking about is..."</em>
                </p>
                <p className="text-sm text-gray-600 mt-2">
                  Acknowledges their point while introducing your perspective.
                </p>
              </div>

              <div className="border-l-4 border-brand-dark-blue pl-6 py-2">
                <h4 className="font-bold text-gray-900 mb-2">When you need more time to think:</h4>
                <p className="text-lg text-gray-700">
                  <em>"That's an important question. Can I think on that and come back to you tomorrow?"</em>
                </p>
                <p className="text-sm text-gray-600 mt-2">
                  Shows you're taking it seriously, not stalling.
                </p>
              </div>

              <div className="border-l-4 border-brand-dark-blue pl-6 py-2">
                <h4 className="font-bold text-gray-900 mb-2">When someone interrupts your point:</h4>
                <p className="text-lg text-gray-700">
                  <em>"Let me finish this thought, then I'd love to hear your take."</em>
                </p>
                <p className="text-sm text-gray-600 mt-2">
                  Polite but firm. Reclaims the floor without being aggressive.
                </p>
              </div>

              <div className="border-l-4 border-brand-dark-blue pl-6 py-2">
                <h4 className="font-bold text-gray-900 mb-2">When you want to build on someone's idea:</h4>
                <p className="text-lg text-gray-700">
                  <em>"I really like that approach. What if we also..."</em>
                </p>
                <p className="text-sm text-gray-600 mt-2">
                  Collaborative, not competitive. Positions you as a team player.
                </p>
              </div>
            </div>

            <p className="text-lg leading-relaxed text-gray-700 mb-8">
              Write these down. Memorize 3-4 of them. <strong>Having pre-planned responses removes the panic of 
              "What do I say?!"</strong> You're not being fake — you're being prepared.
            </p>

            {/* Strategy 7 */}
            <h2 className="text-3xl font-bold text-brand-dark-blue mt-12 mb-6">
              Strategy #7: Reflect After Meetings (Most People Skip This)
            </h2>

            <p className="text-lg leading-relaxed text-gray-700 mb-6">
              You finish a meeting. You move on to the next thing. But <strong>confident speakers take 2 minutes 
              to reflect</strong> while it's fresh. What went well? What felt awkward? Where did you hesitate?
            </p>

            <p className="text-lg leading-relaxed text-gray-700 mb-6">
              This isn't about beating yourself up. It's about pattern recognition. If you notice you always freeze 
              when the director asks your opinion, now you know what to prepare for next time.
            </p>

            <h3 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">
              Quick Post-Meeting Reflection (2 Minutes)
            </h3>

            <div className="bg-gradient-to-r from-brand-gold/10 to-transparent p-6 rounded-lg mb-8">
              <ul className="space-y-3 text-lg text-gray-700">
                <li className="flex gap-3">
                  <span className="text-brand-gold font-bold">✓</span>
                  <span>What's one thing I said that felt confident?</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-brand-gold font-bold">✓</span>
                  <span>What's one moment where I hesitated or fumbled?</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-brand-gold font-bold">✓</span>
                  <span>Did I use filler words? How many (rough guess)?</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-brand-gold font-bold">✓</span>
                  <span>Did I speak up when I wanted to, or hold back?</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-brand-gold font-bold">✓</span>
                  <span>What will I do differently in tomorrow's meeting?</span>
                </li>
              </ul>
            </div>

            <p className="text-lg leading-relaxed text-gray-700 mb-8">
              Over time, you'll notice patterns. Maybe you're great in small team meetings but freeze in leadership 
              meetings. Maybe you're confident when presenting data but nervous when sharing opinions. <strong>Once 
              you see the pattern, you can practice specifically for that scenario.</strong>
            </p>

            {/* CTA Box */}
            <div className="bg-gradient-to-br from-brand-dark-blue to-brand-gold p-8 rounded-2xl text-white my-12">
              <h3 className="text-2xl font-bold mb-4">
                Practice Before Your Next Meeting
              </h3>
              <p className="text-lg mb-6 text-white/90">
                Use Pavone to rehearse answering common meeting questions. Get instant AI feedback on your tone, 
                pacing, and filler words. Show up to your next meeting knowing exactly how you sound.
              </p>
              <Link
                to="/sign-up"
                className="inline-block bg-white text-brand-dark-blue font-semibold px-8 py-4 rounded-xl hover:bg-gray-100 transition-colors"
              >
                START PRACTICING FREE
              </Link>
            </div>

            {/* Conclusion */}
            <h2 className="text-3xl font-bold text-brand-dark-blue mt-12 mb-6">
              Confidence in Meetings is Trainable, Not Innate
            </h2>

            <p className="text-lg leading-relaxed text-gray-700 mb-6">
              Here's what I want you to remember: <strong>the people who speak confidently in meetings aren't 
              naturally confident.</strong> They've just learned the techniques that make them <em>sound</em> 
              confident — even when they're nervous.
            </p>

            <p className="text-lg leading-relaxed text-gray-700 mb-6">
              You don't need to be the loudest voice in the room. You don't need perfect answers. You just need 
              to sound clear, composed, and intentional. And that comes from:
            </p>

            <ul className="space-y-2 mb-8 text-lg text-gray-700">
              <li className="flex gap-3">
                <span className="text-brand-dark-blue font-bold">✓</span>
                <span>Preparing bullet points (not full scripts)</span>
              </li>
              <li className="flex gap-3">
                <span className="text-brand-dark-blue font-bold">✓</span>
                <span>Using bridge phrases to buy yourself thinking time</span>
              </li>
              <li className="flex gap-3">
                <span className="text-brand-dark-blue font-bold">✓</span>
                <span>Slowing down and using strategic pauses</span>
              </li>
              <li className="flex gap-3">
                <span className="text-brand-dark-blue font-bold">✓</span>
                <span>Adjusting your tone to sound authoritative</span>
              </li>
              <li className="flex gap-3">
                <span className="text-brand-dark-blue font-bold">✓</span>
                <span>Eliminating filler words through awareness</span>
              </li>
              <li className="flex gap-3">
                <span className="text-brand-dark-blue font-bold">✓</span>
                <span>Having go-to phrases for tricky situations</span>
              </li>
              <li className="flex gap-3">
                <span className="text-brand-dark-blue font-bold">✓</span>
                <span>Reflecting after each meeting to improve</span>
              </li>
            </ul>

            <p className="text-lg leading-relaxed text-gray-700 mb-8">
              Start with one strategy. Just one. Maybe it's using a bridge phrase before you answer questions. 
              Or cutting your filler words in half. Practice it for a week. Notice the difference. Then add another 
              strategy. <strong>Six months from now, when someone puts you on the spot, you won't freeze.</strong> 
              You'll pause, organize your thoughts, and speak clearly. Because you've trained for exactly this moment.
            </p>

            {/* FAQ Section */}
            <div className="mt-16 mb-12">
              <h2 className="text-3xl font-bold text-brand-dark-blue mb-8">
                Frequently Asked Questions
              </h2>

              <div className="space-y-6">
                <div className="border-l-4 border-brand-gold pl-6 py-2">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    How can I speak up more in meetings?
                  </h3>
                  <p className="text-lg text-gray-700">
                    Start small. Set a goal to speak once in every meeting — even if it's just agreeing with 
                    someone's point or asking a clarifying question. Prepare one bullet point before the meeting 
                    that you want to contribute. Use a bridge phrase like "I'd add that..." to jump into the 
                    conversation. The more you speak, the easier it gets. The longest gap to cross is from zero 
                    comments to one comment. After that, momentum builds.
                  </p>
                </div>

                <div className="border-l-4 border-brand-gold pl-6 py-2">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    How do I stop my voice from shaking during meetings?
                  </h3>
                  <p className="text-lg text-gray-700">
                    Voice shaking comes from shallow breathing and tension. Before you speak, take one deep breath 
                    from your diaphragm (not your chest). This calms your nervous system. When you start speaking, 
                    speak 20% louder than feels natural — this forces you to use your diaphragm and stabilizes your 
                    voice. Also, slow down deliberately. Fast talking + shallow breathing = shaky voice. Slow pacing + 
                    deep breathing = steady voice.
                  </p>
                </div>

                <div className="border-l-4 border-brand-gold pl-6 py-2">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    What's the best way to prepare for daily meetings?
                  </h3>
                  <p className="text-lg text-gray-700">
                    Spend 5 minutes before each meeting writing 3 bullet points for topics you might discuss. Don't 
                    script full answers — just key words. For recurring meetings (like team standups), prepare 2-3 
                    go-to phrases you can use for common questions. Example: "I'm focused on X today, blocked on Y, 
                    and need help with Z." Having a simple structure removes the panic of "What do I say?" and lets 
                    you sound clear and organized.
                  </p>
                </div>

                <div className="border-l-4 border-brand-gold pl-6 py-2">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    Can AI help me improve my communication skills?
                  </h3>
                  <p className="text-lg text-gray-700">
                    Yes! AI communication coaching tools can analyze your speaking patterns and give you objective 
                    feedback on tone, pacing, filler words, and clarity. Instead of wondering "Do I sound confident?" 
                    you get concrete data: "You used 'um' 8 times and spoke 30% faster than optimal." This lets you 
                    practice answering common meeting questions before the actual meeting and improve specific weak 
                    points. It's like having a communication coach available 24/7.
                  </p>
                </div>

                <div className="border-l-4 border-brand-gold pl-6 py-2">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    How do I sound more confident when I disagree with someone in a meeting?
                  </h3>
                  <p className="text-lg text-gray-700">
                    Use the "acknowledge + redirect" pattern: Start by validating their point ("I see where you're 
                    coming from...") then introduce your perspective ("One thing I'm considering is..."). This keeps 
                    it collaborative, not confrontational. Keep your tone steady and slightly lower your pitch on 
                    your main point — higher pitch = nervousness, lower pitch = authority. And don't apologize 
                    before disagreeing (avoid "Sorry, but..." or "I might be wrong, but..."). Just state your view 
                    clearly and let it stand.
                  </p>
                </div>
              </div>
            </div>

            {/* Long-tail Keywords Section (hidden from users, for SEO) */}
            <div className="sr-only">
              <h2>Related Topics and Keywords</h2>
              <ul>
                <li>how to speak confidently in meetings when put on the spot</li>
                <li>improve communication in daily work meetings</li>
                <li>how to sound professional at work in video calls</li>
                <li>daily meeting confidence tips for remote workers</li>
                <li>AI communication coach for professionals</li>
                <li>speak up more in team meetings</li>
                <li>stop voice shaking in meetings</li>
                <li>reduce filler words in professional meetings</li>
                <li>sound more confident when manager asks questions</li>
                <li>meeting communication skills for introverts</li>
                <li>how to prepare for spontaneous questions in meetings</li>
              </ul>
            </div>
          </motion.div>

          {/* Author/Date Footer */}
          <div className="mt-16 pt-8 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-600">
                <p className="font-medium text-gray-900">Pavone Academy</p>
                <p>Expert communication coaching for professionals</p>
              </div>
              <Link
                to="/academy"
                className="text-brand-dark-blue hover:text-brand-gold font-medium transition-colors"
              >
                Read More Articles →
              </Link>
            </div>
          </div>
        </article>
      </main>

      {/* Footer */}
      <footer className="py-8 px-4 border-t">
        <div className="max-w-[1080px] mx-auto px-4 flex flex-col items-center gap-3 md:flex-row md:justify-between">
          <p className="text-sm text-muted-foreground">
            © 2026 Pavone. All rights reserved.
          </p>
          <div className="flex items-center gap-6 text-sm">
            <a href="/privacy" className="hover:underline">
              Privacy Policy
            </a>
            <a href="/terms" className="hover:underline">
              Terms of Service
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}

